import { apiGet, apiSend, setAccessToken } from './client';

export type LoginMethod = 'password' | 'google' | 'phone' | 'telegram';

export interface AuthUser {
  public_id: string;
  email: string | null;
  telegram_id: number | null;
  username: string | null;
  display_name: string | null;
  given_name: string | null;
  family_name: string | null;
  date_of_birth: string | null;
  gender: string | null;
  age_eligible: boolean | null;
  created_at: string | null;
  locale: string;
  country: string;
  email_verified: boolean;
  phone: string | null;
  phone_verified: boolean;
  has_password: boolean;
  registration_method: string | null;
  login_methods: LoginMethod[];
  google_linked: boolean;
  google_email: string | null;
  identity_locked_fields?: string[];
  pending_profile_change?: {
    public_id: string;
    status: string;
    reason: string;
    requested: Record<string, string | null>;
    current?: Record<string, string | null>;
    conversation_public_id?: string | null;
    created_at?: string | null;
  } | null;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  user: AuthUser;
  created?: boolean;
  verification_email_sent?: boolean;
}

export interface ProvidersStatus {
  email: { enabled: boolean; provider: string; real_delivery: boolean };
  google: { enabled: boolean; message: string };
  sms: {
    enabled: boolean;
    phone_auth_enabled?: boolean;
    message: string;
    resend_cooldown_seconds: number;
    otp_length: number;
  };
  telegram: {
    enabled: boolean;
    widget_enabled: boolean;
    oidc_enabled: boolean;
    bot_username: string | null;
  };
}

export type TelegramWidgetUser = {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
};

function keep(data: AuthResponse): AuthResponse {
  setAccessToken(data.access_token);
  return data;
}

export async function registerEmail(input: {
  email: string;
  password: string;
  locale?: string;
  accept_terms: boolean;
  referral_code?: string | null;
}): Promise<AuthResponse> {
  return keep(await apiSend<AuthResponse>('POST', '/api/v1/auth/register', input));
}

export async function loginEmail(input: { email: string; password: string }): Promise<AuthResponse> {
  return keep(await apiSend<AuthResponse>('POST', '/api/v1/auth/login', input));
}

export async function loginTelegram(initData: string, referralCode?: string | null): Promise<AuthResponse> {
  return keep(
    await apiSend<AuthResponse>('POST', '/api/v1/auth/telegram', {
      init_data: initData,
      referral_code: referralCode || undefined,
    }),
  );
}

export async function loginTelegramWidget(
  user: TelegramWidgetUser,
  referralCode?: string | null,
): Promise<AuthResponse> {
  return keep(
    await apiSend<AuthResponse>('POST', '/api/v1/auth/telegram/widget', {
      ...user,
      referral_code: referralCode || undefined,
    }),
  );
}

export async function fetchMe(): Promise<AuthUser> {
  return apiGet<AuthUser>('/api/v1/auth/me');
}

export async function fetchProviders(): Promise<ProvidersStatus> {
  return apiGet<ProvidersStatus>('/api/v1/auth/providers/status');
}

export async function updateLocale(locale: string): Promise<AuthUser> {
  return apiSend<AuthUser>('PATCH', '/api/v1/auth/me/locale', { locale });
}

export async function updateProfile(input: {
  given_name?: string | null;
  family_name?: string | null;
  date_of_birth?: string | null;
  gender?: string | null;
}): Promise<AuthUser> {
  return apiSend<AuthUser>('PATCH', '/api/v1/auth/me/profile', input);
}

export async function createProfileChangeRequest(input: {
  given_name?: string;
  family_name?: string;
  date_of_birth?: string;
  gender?: string;
  reason: string;
}): Promise<{
  public_id: string;
  status: string;
  conversation_public_id?: string | null;
}> {
  return apiSend('POST', '/api/v1/profile/change-requests', input);
}

export function logout(): void {
  setAccessToken(null);
}

export async function logoutServer(all = false): Promise<void> {
  try {
    await apiSend<void>('POST', all ? '/api/v1/auth/logout-all' : '/api/v1/auth/logout');
  } finally {
    setAccessToken(null);
  }
}

export async function reauth(password: string, mfaCode?: string): Promise<AuthResponse> {
  return keep(
    await apiSend<AuthResponse>('POST', '/api/v1/auth/reauth', { password, mfa_code: mfaCode || undefined }),
  );
}

export async function verifyEmail(token: string): Promise<void> {
  await apiSend('POST', '/api/v1/auth/email/verify', { token });
}

export async function resendVerification(): Promise<{ status: string }> {
  return apiSend('POST', '/api/v1/auth/email/verification/resend');
}

export async function forgotPassword(email: string): Promise<{ status: string; delivery_available: boolean }> {
  return apiSend('POST', '/api/v1/auth/password/forgot', { email });
}

export async function checkResetToken(token: string): Promise<boolean> {
  const res = await apiSend<{ valid: boolean }>('POST', '/api/v1/auth/password/reset/check', { token });
  return res.valid;
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await apiSend('POST', '/api/v1/auth/password/reset', { token, new_password: newPassword });
}

export async function changePassword(currentPassword: string | null, newPassword: string): Promise<AuthResponse> {
  return keep(
    await apiSend<AuthResponse>('POST', '/api/v1/auth/password/change', {
      current_password: currentPassword || undefined,
      new_password: newPassword,
    }),
  );
}

export async function changeEmail(newEmail: string, currentPassword: string | null): Promise<AuthUser> {
  return apiSend<AuthUser>('POST', '/api/v1/auth/email/change', {
    new_email: newEmail,
    current_password: currentPassword || undefined,
  });
}

export async function googleStart(purpose: 'login' | 'link'): Promise<{ authorization_url: string; binding: string }> {
  return apiSend('POST', '/api/v1/auth/google/start', { purpose });
}

export async function telegramOidcStart(
  purpose: 'login' | 'link',
): Promise<{ authorization_url: string; binding: string }> {
  return apiSend('POST', '/api/v1/auth/telegram/oidc/start', { purpose });
}

export async function telegramOidcCallback(input: {
  code: string;
  state: string;
  binding: string;
}): Promise<{ purpose: 'login' | 'link'; session: AuthResponse | null; user: AuthUser | null }> {
  const res = await apiSend<{ purpose: 'login' | 'link'; session: AuthResponse | null; user: AuthUser | null }>(
    'POST',
    '/api/v1/auth/telegram/oidc/callback',
    input,
  );
  if (res.session) keep(res.session);
  return res;
}

export async function googleCallback(input: {
  code: string;
  state: string;
  binding: string;
}): Promise<{ purpose: 'login' | 'link'; session: AuthResponse | null; user: AuthUser | null }> {
  const res = await apiSend<{ purpose: 'login' | 'link'; session: AuthResponse | null; user: AuthUser | null }>(
    'POST',
    '/api/v1/auth/google/callback',
    input,
  );
  if (res.session) keep(res.session);
  return res;
}

export async function phoneStart(
  phone: string,
  purpose: 'login' | 'link',
): Promise<{ status: string; phone_masked: string; expires_in: number; resend_in: number }> {
  return apiSend('POST', '/api/v1/auth/phone/start', { phone, purpose });
}

export async function phoneVerify(
  phone: string,
  code: string,
  purpose: 'login' | 'link',
): Promise<{ purpose: string; session: AuthResponse | null; user: AuthUser | null }> {
  const res = await apiSend<{ purpose: string; session: AuthResponse | null; user: AuthUser | null }>(
    'POST',
    '/api/v1/auth/phone/verify',
    { phone, code, purpose },
  );
  if (res.session) keep(res.session);
  return res;
}

export async function linkTelegram(widget: TelegramWidgetUser): Promise<AuthUser> {
  return apiSend<AuthUser>('POST', '/api/v1/auth/telegram/link', { widget });
}

export async function unlinkMethod(method: 'google' | 'phone' | 'telegram'): Promise<AuthUser> {
  return apiSend<AuthUser>('DELETE', `/api/v1/auth/${method}`);
}
